import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { trainerApi } from '@/api/trainer'
import { CalendarDays, BookOpen, Clock } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { SignatureEditor } from '@/features/admin/company/MyCompanyPage'
import { useTrainerDates } from '@/hooks/useDates'

function statusBadge(status: string) {
  const map: Record<string, string> = {
    Booked:    'bg-green-50 text-green-700 border-green-100',
    Completed: 'bg-slate-100 text-slate-600 border-slate-200',
  }
  return map[status] ?? 'bg-slate-100 text-slate-600 border-slate-200'
}

export default function TrainerOverviewPage({ basePath = '/trainer' }: { basePath?: string }) {
  const { formatDate, today } = useTrainerDates()
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { data: bookings = [], isLoading: bookingsLoading } = useQuery({
    queryKey: ['trainer-bookings'],
    queryFn: () => trainerApi.getMyBookings().then(r => r.data),
    staleTime: 0,
  })

  const { data: courses = [] } = useQuery({
    queryKey: ['trainer-courses'],
    queryFn: () => trainerApi.getMyCourses().then(r => r.data),
  })

  const { data: signature } = useQuery({
    queryKey: ['trainer-signature'],
    queryFn: () => trainerApi.getMySignature().then(r => r.data),
  })

  const signatureMutation = useMutation({
    mutationFn: ({ type, text }: { type: Parameters<typeof trainerApi.updateMySignature>[0]; text: string | null }) =>
      trainerApi.updateMySignature(type, text),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trainer-signature'] }),
  })

  const signatureImageMutation = useMutation({
    mutationFn: (file: File) => trainerApi.uploadMySignatureImage(file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trainer-signature'] }),
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Signature upload failed'
      alert(msg)
    },
  })

  const todayStr = today()

  const upcoming = bookings.filter(b =>
    b.bookingDate.slice(0, 10) >= todayStr && b.status !== 'Completed'
  ).slice(0, 5)

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">
          Welcome back{user?.email ? `, ${user.email.split('@')[0]}` : ''}
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">Here's your upcoming schedule.</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
              <CalendarDays size={16} className="text-indigo-600" />
            </div>
            <span className="text-sm font-medium text-gray-500">Upcoming sessions</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{upcoming.length}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
              <BookOpen size={16} className="text-violet-600" />
            </div>
            <span className="text-sm font-medium text-gray-500">Qualified courses</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{courses.length}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
              <Clock size={16} className="text-slate-500" />
            </div>
            <span className="text-sm font-medium text-gray-500">Total sessions</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{bookings.length}</p>
        </div>
      </div>

      {/* Upcoming sessions */}
      <div>
        <h2 className="text-base font-semibold text-gray-900 mb-3">Upcoming sessions</h2>
        {bookingsLoading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : upcoming.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
            <CalendarDays size={32} className="mx-auto text-gray-200 mb-3" />
            <p className="text-gray-500 font-medium">No upcoming sessions</p>
            <p className="text-sm text-gray-400 mt-1">You have no bookings scheduled.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {upcoming.map(b => {
              const isSession = b.kind === 'Session'
              const className = 'bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center gap-4 hover:border-indigo-200 hover:bg-indigo-50/30 transition-colors block'
              const content = (
                <>
                  <div className="w-12 text-center shrink-0">
                    <p className="text-xs text-gray-400 uppercase tracking-wide">
                      {formatDate(b.bookingDate + 'T00:00:00', { month: 'short' })}
                    </p>
                    <p className="text-xl font-bold text-gray-900 leading-none">
                      {new Date(b.bookingDate + 'T00:00:00').getDate()}
                    </p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900">{b.courseName}</p>
                    <p className="text-sm text-gray-500">{isSession ? 'Public session' : b.clientName} · {b.places} place{b.places !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusBadge(b.status)}`}>
                      {b.status}
                    </span>
                    <span className="text-xs text-gray-400">{b.role}</span>
                  </div>
                </>
              )
              return (
                <Link key={b.bookingId} to={`${basePath}/${isSession ? 'sessions' : 'bookings'}/${b.bookingId}`} className={className}>
                  {content}
                </Link>
              )
            })}
          </div>
        )}
      </div>

      {/* My Signature */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">My Signature</CardTitle>
        </CardHeader>
        <CardContent>
          <SignatureEditor
            signatureType={signature?.signatureType ?? 'None'}
            signatureText={signature?.signatureText ?? null}
            signatureImageUrl={signature?.signatureImageUrl ?? null}
            isPending={signatureMutation.isPending || signatureImageMutation.isPending}
            onSave={(type, text) => signatureMutation.mutate({ type, text })}
            onUpload={file => signatureImageMutation.mutate(file)}
            label="My signature"
            hint="Your signature will appear on certificates for sessions where you are the lead trainer."
          />
        </CardContent>
      </Card>
    </div>
  )
}
